import { ChangeEvent, useState, useCallback } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import debounce from "lodash/debounce";

import {
  Field,
  HTMLWrapper,
  InputGroup,
  CheckIcon,
  Spinner,
  Input,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";

import { setAnswer, setError } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import { verifyUserAvailability } from "@/models";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

interface LoginQuestionProps {
  question: Partial<Question>;
  isAsterixRequired?: boolean;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  try {
    await string()
      .trim()
      .test("notOnlyWhitespace", "blank", (value) => !/^\s*$/.test(value))
      .required()
      .max(question.maxLength || 255)
      //login shouldn't be an email
      .matches(/^(?!\S+@\S+\.\S+$)/)
      .validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const LoginQuestion = ({
  question,
  isAsterixRequired = true
}: LoginQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [isValidate, setisValidate] = useState(false);
  const [isUsernameAvailable, setUsernameAvailability] = useState(false);
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { signupFormResponses } = useAppSelector(
    (state) => state.signupFormResponses
  );
  const { login } = user.registeredUser.profile;

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setisValidate(true);
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );

    if (
      e.target.value === signupFormResponses[question.id]?.value ||
      e.target.value === login
    ) {
      return setisValidate(false);
    }

    handleUsernameVerification(e.target.value);
  };

  const validateUsername = async (value: string) => {
    const error = await validate(value, question);

    if (error?.length) {
      dispatch(
        setError({
          questionId: question.id,
          error
        })
      );
    } else {
      const response = await verifyUserAvailability(token, baseUrl, {
        username: value
      });

      if (response.usernameAvailable) {
        setUsernameAvailability(true);
      } else {
        dispatch(
          setError({
            questionId: question.id,
            error: "taken"
          })
        );
      }
    }
    setisValidate(false);
  };

  const handleUsernameVerification = useCallback(
    debounce(validateUsername, 1000),
    []
  );

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required && isAsterixRequired}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <InputGroup
        endElement={
          answer ? (
            <>
              {isValidate && <Spinner size="sm" />}
              {isUsernameAvailable && !error.length && !isValidate && (
                <CheckIcon color="green.500" />
              )}
            </>
          ) : undefined
        }
        endElementProps={{ height: "100%" }}
      >
        <Input
          type="text"
          value={answer}
          onChange={handleChange}
          data-testid="loginQuestion-input"
          maxLength={question.maxLength}
          size={{ base: "lg", lg: "md" }}
          name="loginQuestion-input"
          autoComplete="off"
        />
      </InputGroup>
      {error.length > 0 && (
        <Field.ErrorText data-testid="loginQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      {question.notes && <Field.HelperText>{question.notes}</Field.HelperText>}
    </Field.Root>
  );
};

export default LoginQuestion;
